import React, { useEffect, useMemo, useState } from "react";
import { Buildings, Plus, PencilSimple, Trash, TreeStructure, Broom, Stack, FilePdf, FileCsv, DownloadSimple } from "@phosphor-icons/react";
import client from "../lib/api";
import { Button, Input } from "./ui";
import { TL } from "../i18n";
import PortalControl from "./PortalControl";
import { toast } from "sonner";

const money = (n, c) => `${c ? c + " " : ""}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const blank = { name: "", type: "Trust", parent_id: "", ownership_pct: 100, notes: "" };

export default function OwnershipConsolidation() {
  const [ents, setEnts] = useState([]);
  const [types, setTypes] = useState([]);
  const [con, setCon] = useState(null);
  const [reg, setReg] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [liabs, setLiabs] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [assign, setAssign] = useState({ kind: "holding", item: "", entity: "" });
  const [selUnc, setSelUnc] = useState([]);
  const [catFor, setCatFor] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("");

  const load = () => {
    client.get("/entities").then((r) => { setEnts(r.data.entities || []); setTypes(r.data.types || []); }).catch(() => {});
    client.get("/entities/consolidation").then((r) => setCon(r.data)).catch(() => {});
    client.get("/cash/registry").then((r) => setReg(r.data)).catch(() => {});
    client.get("/holdings").then((r) => setHoldings(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
    client.get("/liabilities").then((r) => setLiabs(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
    client.get("/import-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const cur = con?.base_currency || "USD";
  const save = () => {
    if (!form.name.trim()) return toast.error("Name required");
    const body = { ...form, ownership_pct: parseFloat(form.ownership_pct) || 0, parent_id: form.parent_id || null };
    const req = editId ? client.put(`/entities/${editId}`, body) : client.post("/entities", body);
    req.then(() => { toast.success(editId ? "Entity updated" : "Entity added"); setForm(blank); setEditId(null); load(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  };
  const edit = (e) => { setEditId(e.id); setForm({ name: e.name, type: e.type, parent_id: e.parent_id || "", ownership_pct: e.ownership_pct, notes: e.notes || "" }); };
  const del = (id) => { if (!window.confirm("Delete this entity? Holdings will be unassigned.")) return; client.delete(`/entities/${id}`).then(() => { toast.success("Deleted"); load(); }); };
  const doAssign = () => {
    if (!assign.item || !assign.entity) return;
    const req = assign.kind === "liability"
      ? client.put(`/entities/liability/${assign.item}/assign`, { entity_id: assign.entity })
      : client.put(`/holdings/${assign.item}`, { data: { entity_id: assign.entity } });
    req.then(() => { toast.success(`${assign.kind === "liability" ? "Liability" : "Holding"} assigned`); setAssign({ kind: assign.kind, item: "", entity: "" }); load(); })
      .catch(() => toast.error("Assign failed"));
  };
  const categorize = () => {
    if (!selUnc.length || !catFor) return toast.error("Pick items and a category");
    client.post("/cash/categorize", { ids: selUnc, category: catFor })
      .then((r) => { toast.success(`Categorized ${r.data.updated} item(s)`); setSelUnc([]); setCatFor(""); load(); })
      .catch(() => toast.error("Failed"));
  };

  const entName = useMemo(() => Object.fromEntries(ents.map((e) => [e.id, e.name])), [ents]);

  const downloadTemplate = async () => {
    if (!tpl) return;
    try {
      const r = await client.get(`/import-templates/${tpl}/blank.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `FopFo_import_${tpl}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const download = async (eid, kind, name) => {
    try {
      const path = eid === "__pack__" ? `/entities/statement-pack.${kind}` : `/entities/${eid}/statement.${kind}`;
      const r = await client.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Statement_${(name || "entity").replace(/\s+/g, "_").slice(0, 40)}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate statement"); }
  };

  return (
    <div className="mb-4 rounded-2xl border border-[#08BDBA]/30 bg-gradient-to-br from-[#08BDBA]/8 to-transparent p-4" data-testid="ownership-consolidation">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-base font-semibold">
          <TreeStructure size={20} weight="duotone" className="text-[#08BDBA]" />
          Entities & Ownership Consolidation
          <span className="rounded-full bg-[#08BDBA]/10 px-2 py-0.5 text-[10px] font-medium text-[#08BDBA]">{TL("net roll-up · governed")}</span>
        </div>
        <Button variant="ghost" className="border border-[#08BDBA]/40 text-xs text-[#08BDBA]"
          onClick={() => download("__pack__", "pdf", "Consolidated_Family_Statement")} data-testid="con-pack-pdf">
          <FilePdf size={14} className="me-1" />Consolidated pack (PDF)
        </Button>
      </div>

      {/* consolidation summary */}
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5" data-testid="con-total">
          <div className="text-xs opacity-60">{TL("Consolidated (attributable)")}</div>
          <div className="text-xl font-bold text-[#08BDBA]">{money(con?.total_attributable, cur)}</div>
        </div>
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5">
          <div className="text-xs opacity-60">{TL("Entities")}</div>
          <div className="text-xl font-bold">{con?.entity_count ?? 0}</div>
        </div>
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5" data-testid="con-unassigned">
          <div className="text-xs opacity-60">{TL("Unassigned holdings")}</div>
          <div className="text-xl font-bold">{con?.unassigned?.count ?? 0}<span className="ms-1 text-xs font-normal opacity-50">{money(con?.unassigned?.value, cur)}</span></div>
        </div>
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5">
          <div className="text-xs opacity-60">{TL("Reg. uncategorized")}</div>
          <div className="text-xl font-bold text-[#DA1E28]">{reg?.uncategorized_count ?? 0}</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* entities + consolidation table */}
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Buildings size={16} weight="duotone" className="text-[#08BDBA]" />{TL("Ownership structure")}</div>
          <div className="max-h-56 overflow-auto">
            <table className="w-full text-xs" data-testid="entities-table">
              <thead><tr className="border-b border-black/10 text-start opacity-60 dark:border-white/10">
                <th className="py-1 pe-2">{TL("Entity")}</th><th className="px-1">{TL("Type")}</th><th className="px-1 text-end">{TL("Own%")}</th>
                <th className="px-1 text-end">{TL("Eff%")}</th><th className="px-1 text-end">{TL("Net")}</th><th className="px-1 text-end">{TL("Attributable")}</th><th></th></tr></thead>
              <tbody>
                {(con?.entities || []).map((e) => (
                  <tr key={e.id} className="border-b border-black/5 dark:border-white/5" data-testid={`entity-row-${e.id}`}>
                    <td className="py-1 pe-2 font-medium">{e.name}{e.parent_id ? <span className="opacity-50"> ↳ {entName[e.parent_id]}</span> : null}</td>
                    <td className="px-1 opacity-70">{e.type}</td>
                    <td className="px-1 text-end tabular-nums">{e.ownership_pct}%</td>
                    <td className="px-1 text-end tabular-nums">{e.effective_pct}%</td>
                    <td className="px-1 text-end tabular-nums">{money(e.net_value, cur)}{e.liabilities ? <span className="text-[#DA1E28]"> −{money(e.liabilities)}</span> : null}</td>
                    <td className="px-1 text-end tabular-nums font-semibold">{money(e.attributable_value, cur)}</td>
                    <td className="px-1 text-end whitespace-nowrap">
                      <button onClick={() => download(e.id, "pdf", e.name)} className="text-[#DA1E28] opacity-70 hover:opacity-100" title={TL("Trustee PDF")} data-testid={`entity-pdf-${e.id}`}><FilePdf size={14} /></button>
                      <button onClick={() => download(e.id, "csv", e.name)} className="ms-1 text-[#24A148] opacity-70 hover:opacity-100" title={TL("CSV")} data-testid={`entity-csv-${e.id}`}><FileCsv size={14} /></button>
                      <button onClick={() => edit(e)} className="ms-1 opacity-60 hover:opacity-100" data-testid={`entity-edit-${e.id}`}><PencilSimple size={13} /></button>
                      <button onClick={() => del(e.id)} className="ms-1 text-[#DA1E28] opacity-70 hover:opacity-100" data-testid={`entity-del-${e.id}`}><Trash size={13} /></button>
                    </td>
                  </tr>
                ))}
                {!con?.entities?.length && <tr><td colSpan={7} className="py-3 text-center opacity-50">{TL("No entities yet — add a trust, LP or holdco.")}</td></tr>}
              </tbody>
            </table>
          </div>

          {/* add / edit form */}
          <div className="mt-3 grid grid-cols-2 gap-2 border-t border-black/10 pt-3 dark:border-white/10">
            <Input placeholder={TL("Entity name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="entity-name" />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-md border border-black/15 bg-transparent px-2 py-2 text-sm dark:border-white/15" data-testid="entity-type">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="rounded-md border border-black/15 bg-transparent px-2 py-2 text-sm dark:border-white/15" data-testid="entity-parent">
              <option value="">{TL("No parent (top level)")}</option>
              {ents.filter((e) => e.id !== editId).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <Input type="number" placeholder={TL("Own %")} value={form.ownership_pct} onChange={(e) => setForm({ ...form, ownership_pct: e.target.value })} className="flex-1" data-testid="entity-ownership" />
              <Button onClick={save} data-testid="entity-save"><Plus size={14} />{editId ? TL("Update") : TL("Add")}</Button>
            </div>
          </div>

          {/* assign holding */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-black/10 pt-3 text-xs dark:border-white/10">
            <span className="flex items-center gap-1 opacity-60"><Stack size={13} />{TL("Assign:")}</span>
            <select value={assign.kind} onChange={(e) => setAssign({ ...assign, kind: e.target.value, item: "" })} className="rounded-md border border-black/15 bg-transparent px-2 py-1 dark:border-white/15" data-testid="assign-kind">
              <option value="holding">{TL("Holding")}</option>
              <option value="liability">{TL("Liability")}</option>
            </select>
            <select value={assign.item} onChange={(e) => setAssign({ ...assign, item: e.target.value })} className="rounded-md border border-black/15 bg-transparent px-2 py-1 dark:border-white/15" data-testid="assign-holding">
              <option value="">{TL("Choose")} {assign.kind === "liability" ? TL("liability") : TL("holding")}…</option>
              {(assign.kind === "liability" ? liabs : holdings).map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <span className="opacity-60">→</span>
            <select value={assign.entity} onChange={(e) => setAssign({ ...assign, entity: e.target.value })} className="rounded-md border border-black/15 bg-transparent px-2 py-1 dark:border-white/15" data-testid="assign-entity">
              <option value="">{TL("Choose entity…")}</option>
              {ents.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <Button variant="ghost" onClick={doAssign} disabled={!assign.item || !assign.entity} data-testid="assign-btn">{TL("Assign")}</Button>
          </div>
        </div>

        {/* cash registry */}
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5" data-testid="cash-registry">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Broom size={16} weight="duotone" className="text-[#08BDBA]" />{TL("Cash-management registry — tidy uncategorized")}</div>
          {reg?.uncategorized_count ? (
            <>
              <div className="max-h-48 overflow-auto">
                {reg.uncategorized.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 border-b border-black/5 py-1 text-xs dark:border-white/5" data-testid={`unc-row-${u.id}`}>
                    <input type="checkbox" checked={selUnc.includes(u.id)} onChange={(e) => setSelUnc((s) => e.target.checked ? [...s, u.id] : s.filter((x) => x !== u.id))} data-testid={`unc-check-${u.id}`} />
                    <span className="flex-1 font-medium">{u.name}</span>
                    <span className="tabular-nums opacity-60">{money(u.value, u.currency || cur)}</span>
                  </label>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <select value={catFor} onChange={(e) => setCatFor(e.target.value)} className="flex-1 rounded-md border border-black/15 bg-transparent px-2 py-1 text-xs dark:border-white/15" data-testid="cat-select">
                  <option value="">{TL("Assign category…")}</option>
                  {(reg.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Button onClick={categorize} disabled={!selUnc.length || !catFor} data-testid="cat-apply">{TL("Apply")} ({selUnc.length})</Button>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-xs opacity-50" data-testid="cash-clean">{TL("✓ Every holding is categorized. Nothing to tidy.")}</div>
          )}
        </div>
      </div>

      {/* Portal + import templates */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PortalControl />
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 dark:border-white/10 dark:bg-white/5" data-testid="import-templates">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><DownloadSimple size={16} weight="duotone" className="text-[#08BDBA]" />{TL("Custodian import templates")}</div>
          <p className="mb-2 text-xs opacity-60">{TL("Download an importer-ready CSV shaped for your custodian, paste your statement data, then use Import.")}</p>
          <div className="flex items-center gap-2">
            <select value={tpl} onChange={(e) => setTpl(e.target.value)} className="flex-1 rounded-md border border-black/15 bg-transparent px-2 py-1.5 text-xs dark:border-white/15" data-testid="template-select">
              <option value="">{TL("Choose custodian…")}</option>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.custodian}</option>)}
            </select>
            <Button variant="ghost" onClick={downloadTemplate} disabled={!tpl} data-testid="template-download">{TL("Download CSV")}</Button>
          </div>
          {tpl ? <p className="mt-1 text-[11px] opacity-50">{templates.find((t) => t.key === tpl)?.description}</p> : null}
        </div>
      </div>
    </div>
  );
}
